import { useEffect, useState } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { ImageSection as ImageSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { SelectImageModal } from '@frontend/select-image-modal';
import { theme } from '@frontend/theme';
import { Modal, useModalControl } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { ComposerSection } from '../../content-composer/types';
import { convertMediaToImage } from '../../utils/convert-media-to-image';
import { JustifiedLayout } from '../atoms/justified-layout';
import { ImageWithDimensions } from '../types';
import { getFrameStyle } from '../utils/get-frame-style';
import { createImagesWithDimensions, newImageWithDimensions, removeDimensions } from '../utils/image-dimensions';
import { ImageOutput } from './image-output';
import { ImageSettings } from './image-settings';
import { ImageTool } from './image-tool';

export const ImageSection: ComposerSection<ImageSectionType> = ({
  corners = 'square',
  frame,
  height = 'auto',
  imageCount = 1,
  imageSpacing = 0,
  images = [],
}) => {
  const [imagesWithDimensions, setImagesWithDimensions] = useState(() =>
    createImagesWithDimensions(imageCount, images)
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>();
  const { setProps } = useComposerSection<ImageSectionType>();
  const { locationIds } = useContentComposer();
  const { modalProps, triggerProps } = useModalControl();

  useEffect(() => {
    // If the user has increased or decreased the imageCount in the tool settings, update imagesWithDimensions to match
    if (imageCount !== imagesWithDimensions.length) {
      setImagesWithDimensions(createImagesWithDimensions(imageCount, imagesWithDimensions));
    }
  }, [imageCount, imagesWithDimensions]);

  const loadImage = (imageIndex: number, width: number, height: number) => {
    setImagesWithDimensions((previousImagesWithDimensions) => {
      const updatedImagesWithDimensions = [...previousImagesWithDimensions];
      updatedImagesWithDimensions[imageIndex] = {
        ...newImageWithDimensions(updatedImagesWithDimensions[imageIndex]),
        width,
        height,
      };
      return updatedImagesWithDimensions;
    });
  };

  const saveImages = (updatedImagesWithDimensions: ImageWithDimensions[]) => {
    const updatedImages = updatedImagesWithDimensions.map((imageWithDimensions) =>
      removeDimensions(imageWithDimensions)
    );
    setProps((props) => (props.images = updatedImages));
    setImagesWithDimensions(updatedImagesWithDimensions);
  };

  const deselectImage = () => {
    setSelectedImageIndex(undefined);
  };

  const removeImage = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    const updatedImagesWithDimensions = [...imagesWithDimensions];
    updatedImagesWithDimensions[imageIndex] = newImageWithDimensions();
    saveImages(updatedImagesWithDimensions);
  };

  const startReplaceImage = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    triggerProps.onClick();
  };

  const finishReplaceImage = (media?: Media) => {
    if (!media || selectedImageIndex === undefined) return;
    const updatedImagesWithDimensions = [...imagesWithDimensions];
    updatedImagesWithDimensions[selectedImageIndex] = newImageWithDimensions(convertMediaToImage(media));
    saveImages(updatedImagesWithDimensions);
    modalProps.onClose();
  };

  return (
    <SectionBase onClick={deselectImage} css={getFrameStyle(frame)}>
      <JustifiedLayout
        alignment={frame?.alignment}
        corners={corners}
        height={height}
        imageSpacing={imageSpacing}
        images={imagesWithDimensions}
        onLoad={loadImage}
        onRemove={removeImage}
        onReplace={startReplaceImage}
        onSelect={setSelectedImageIndex}
        selectedImageIndex={selectedImageIndex}
      />
      <Modal {...modalProps} minWidth={700}>
        <SelectImageModal
          locationIds={locationIds}
          modalProps={modalProps}
          onSelect={finishReplaceImage}
          showLocationFilter
        />
      </Modal>
    </SectionBase>
  );
};

ImageSection.craft = {
  props: {
    corners: 'square',
    frame: {
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: '24px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '24px',
    },
    height: 'auto',
    imageCount: 1,
    imageSpacing: 8,
    sectionType: 'ImageSection',
  },
  related: {
    output: ImageOutput,
    settings: ImageSettings,
    tool: ImageTool,
  },
};
